import axios from 'axios';
import store from '../slices/store';
import {logout, refreshAccessToken, selectAuthToken, REFRESH_SESSION_CHANGED} from '../slices/authSlice';
import { Sentry, scrubUrl } from '../utils/sentry';

// Frontend is always served same-origin as the API (nginx routes /api/* to the
// Django app). Using a relative baseURL keeps one build artifact across all
// environments — the FQDN is a property of the runtime, not the bundle.
// In dev, package.json "proxy" forwards /api/* to the local backend on :8000.
const API_BASE_URL = '/api/v1';

// Separate axios instance for registration without interceptors
const axiosWithoutInterceptors = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add a request interceptor to add the access token to all requests
axiosInstance.interceptors.request.use(
  (config) => {
    const token = selectAuthToken(store.getState());
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle token refresh on 401 errors
axiosInstance.interceptors.response.use(
  response => response, // Pass through successful responses
  async error => {
    const originalRequest = error.config;
    
    // If error is due to an expired access token (401)
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        // Attempt to refresh the access token
        const newAccessToken = await store.dispatch(refreshAccessToken()).unwrap();

        // Update authorization header with new access token
        originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
        // Retry the original request with the new token
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        // The session changed (logout/account switch) while refreshing:
        // just fail this stale request, don't touch the new session.
        if (refreshError === REFRESH_SESSION_CHANGED) {
          return Promise.reject(error);
        }
        // Refresh token is also expired or invalid; log out the user.
        // Carry the current URL in ?next= so SignIn can return the user to
        // the page they were on (this is a hard reload, so router state
        // would not survive).
        store.dispatch(logout());
        const here = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        const next = here && here !== '/signin' && !here.startsWith('/signin?')
          ? `?next=${encodeURIComponent(here)}`
          : '';
        window.location.href = `/signin${next}`;
      }
    }
    return Promise.reject(error);
  }
);

// Sentry observability for HTTP traffic. 4xx errors are mostly user-driven
// (validation, auth) and noisy — we only capture 5xx and network failures as
// events. Everything else lands as a breadcrumb so it's visible if a later
// error fires in the same session.
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    // Scrub secrets from the URL query before it reaches Sentry (breadcrumb +
    // extra). Defense-in-depth alongside sentry.js beforeSend. A260520-FE-M1.
    const url = scrubUrl(error?.config?.url);
    const method = error?.config?.method;

    Sentry.addBreadcrumb({
      category: 'http',
      type: 'http',
      level: status >= 500 ? 'error' : 'warning',
      data: { url, method, status: status ?? 'network' },
    });

    if (!status || status >= 500) {
      Sentry.captureException(error, {
        tags: { kind: status ? 'http_5xx' : 'network_error' },
        extra: { url, method, status: status ?? null },
      });
    }
    return Promise.reject(error);
  }
);

export { axiosInstance, axiosWithoutInterceptors };